import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  BookOpenIcon,
  ChartBarIcon,
  ClockIcon,
  AcademicCapIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const CourseDetail = () => {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [enrollment, setEnrollment] = useState(null);
  const [subjectProgress, setSubjectProgress] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (courseId) {
      fetchCourseData();
      fetchSubjects();
      fetchEnrollment();
    }
  }, [courseId]);

  const fetchCourseData = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/api/courses/${courseId}/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCourse(data);
      }
    } catch (error) {
      // Error fetching course
    }
  };

  const fetchSubjects = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/api/courses/${courseId}/subjects/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        const subjects = data.results || [];
        setSubjects(subjects);
        subjects.forEach(subject => fetchSubjectProgress(subject.id));
      } else {
        setSubjects([]);
      }
    } catch (error) {
      setSubjects([]);
    }
  };

  const fetchSubjectProgress = async (subjectId) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/api/courses/subjects/${subjectId}/progress/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSubjectProgress(prev => ({
          ...prev,
          [subjectId]: {
            correctAnswers: data.correct_answers || 0,
            totalQuestions: data.total_questions || 0,
            percentage: data.total_questions > 0 ? Math.round((data.correct_answers / data.total_questions) * 100) : 0
          }
        }));
      }
    } catch (error) {
      // Error fetching subject progress
    }
  };

  const fetchEnrollment = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/api/courses/user-courses/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        const enrollments = data.results || [];
        const courseEnrollment = enrollments.find(enrollment => 
          enrollment.course && enrollment.course.id === courseId
        );
        setEnrollment(courseEnrollment);
      }
    } catch (error) {
      // Error fetching enrollment
    } finally {
      setLoading(false);
    }
  };

  // Calculate total progress across all subjects
  const calculateTotalProgress = () => {
    const progressValues = Object.values(subjectProgress);
    if (progressValues.length === 0) {
      return { totalCorrect: 0, totalQuestions: 0, percentage: 0 };
    }
    
    const totalCorrect = progressValues.reduce((sum, progress) => sum + progress.correctAnswers, 0);
    const totalQuestions = progressValues.reduce((sum, progress) => sum + progress.totalQuestions, 0);
    const percentage = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;
    
    return { totalCorrect, totalQuestions, percentage };
  };

  const totalProgress = calculateTotalProgress();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          Course Not Found
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          The course you're looking for doesn't exist or you don't have access to it.
        </p>
        <Link
          to="/dashboard"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Course Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {course.name}
            </h1>
            <div className="flex items-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center">
                <BookOpenIcon className="h-5 w-5 mr-2" />
                <span>{subjects.length} Subjects</span>
              </div>
              <div className="flex items-center">
                <ChartBarIcon className="h-5 w-5 mr-2" />
                <span>{totalProgress.totalQuestions} Questions</span>
              </div>
              {enrollment && (
                <div className="flex items-center">
                  <ClockIcon className="h-5 w-5 mr-2" />
                  <span>
                    {enrollment.is_active 
                      ? `${Math.ceil((new Date(enrollment.expires_at) - new Date()) / (1000 * 60 * 60 * 24))} days remaining`
                      : 'Expired'
                    }
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {enrollment && (
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {totalProgress.totalCorrect}/{totalProgress.totalQuestions}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Correct Answers
              </div>
            </div>
          )}
        </div>

        {enrollment && (
          <div className="mt-6 relative group">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 cursor-pointer">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${totalProgress.percentage}%` }}
              ></div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
              {totalProgress.percentage}% Complete
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
            </div>
          </div>
        )}
      </div>

      {/* Enrollment Status */}
      {!enrollment && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                Not Enrolled
              </h3>
              <p className="text-yellow-700 dark:text-yellow-300">
                You need to purchase this course to access the content.
              </p>
            </div>
            <Link
              to="/store"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700"
            >
              Purchase Course
            </Link>
          </div>
        </div>
      )}

      {/* Subjects List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Course Subjects
          </h2>
        </div>
        
        <div className="p-6">
          {subjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {subjects.map((subject) => (
                <SubjectCard 
                  key={subject.id} 
                  subject={subject} 
                  hasAccess={!!enrollment && enrollment.is_active}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Subjects Available
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                This course doesn't have any subjects configured yet.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SubjectCard = ({ subject, hasAccess }) => {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {subject.code} - {subject.name}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {subject.description}
          </p>
        </div>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Order: {subject.order}
        </div>
        
        {hasAccess ? (
          <Link
            to={`/subjects/${subject.id}`}
            className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Study
            <ArrowRightIcon className="ml-1 h-4 w-4" />
          </Link>
        ) : (
          <span className="text-sm text-gray-400 dark:text-gray-500">
            Purchase Required
          </span>
        )}
      </div>
    </div>
  );
};

export default CourseDetail;
